'use client';

import { CardType } from '@/config/dashboardWorkflows';
import { ReactNode } from 'react';
import TaskCard from './TaskCard';

export interface DashboardCardData {
  id: string;
  title: string;
  type: CardType;
  taskId: string;
  taskIdNum?: number;
  assignedTo?: {
    full_name?: string;
    email?: string;
  } | null;
  dashboardData?: Record<string, unknown>;
}

interface DashboardColumnProps {
  title: string;
  headerColor: 'slate' | 'blue' | 'purple' | 'amber' | 'emerald' | 'red';
  stage: number;
  cards: DashboardCardData[];
  count?: number;
  onCardClick?: (taskId: string) => void;
  onCardCreate?: (title: string, stage: number) => void;
  onCardDrop?: (e: React.DragEvent, taskId: string, newStage: number) => void;
  onCardDragStart?: (e: React.DragEvent, taskId: string) => void;
  onEditContent?: (taskId: number) => void;
  renderCard?: (card: DashboardCardData) => ReactNode;
}

export default function DashboardColumn({
  title,
  headerColor,
  stage,
  cards,
  count,
  onCardClick,
  onCardCreate,
  onCardDrop,
  onCardDragStart,
  onEditContent,
  renderCard
}: DashboardColumnProps) {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (onCardDrop) {
      const taskId = e.dataTransfer.getData('taskId');
      // Use the stage prop for this column
      onCardDrop(e, taskId, stage);
    }
  };

  return (
    <div className="flex-1 min-w-[200px]">
      <div
        className="text-center"
        style={{
          width: '100%',
          padding: '0px',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            width: '100%',
            opacity: 0.5,
            fontSize: '11pt',
            color: '#FFFFFF',
            padding: '0px',
            textTransform: 'uppercase',
          }}
        >
          {title}{count !== undefined ? ` (${count})` : ''}
        </div>
      </div>
      <div
        className="bg-black/20 backdrop-blur-sm rounded-lg p-2 space-y-2 min-h-[500px] mt-2"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {cards.map((card) => (
          renderCard ? (
            <div
              key={card.id}
              draggable={!!onCardDragStart}
              onDragStart={(e) => {
                if (onCardDragStart) {
                  e.dataTransfer.setData('taskId', card.taskId);
                  onCardDragStart(e, card.taskId);
                }
              }}
            >
              {renderCard(card)}
            </div>
          ) : (
            <TaskCard
              key={card.id}
              title={card.title}
              cardType={card.type}
              taskId={card.taskId}
              taskIdNum={card.taskIdNum}
              assignedTo={card.assignedTo}
              onClick={() => onCardClick?.(card.taskId)}
              isDashboardView={true}
              draggable={true}
              onDragStart={(e, taskId) => {
                e.dataTransfer.setData('taskId', typeof taskId === 'string' ? taskId : taskId.toString());
                onCardDragStart?.(e, card.taskId);
              }}
              dashboardData={card.dashboardData}
              onEditContent={onEditContent}
            />
          )
        ))}
        {onCardCreate && (
          <button
            onClick={() => {
              const title = prompt('Enter task title:');
              if (title && title.trim()) {
                onCardCreate(title.trim(), stage);
              }
            }}
            className="w-full py-2 px-3 text-sm text-gray-400 hover:text-white border-2 border-dashed border-gray-600 hover:border-gray-500 rounded-lg transition-colors bg-black/10"
          >
            + Add Task
          </button>
        )}
      </div>
    </div>
  );
}
